import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  ExternalNetworkSchema,
  ConnectorReadinessSchema,
  CreateExternalNetworkSchema,
  UpdateExternalNetworkSchema,
  UpdateNetblockSchema,
  UpdateDomainSchema,
  ExternalNetworkListQuerySchema,
  ExternalNetworkListResponseSchema,
  ExternalNetworkStatsSchema,
  ExternalNetworkAllStatsResponseSchema,
  ExternalNetworkKpiMetricsResponseSchema,
  ExternalNetworkAssetsTrendQuerySchema,
  ExternalNetworkAssetsTrendResponseSchema,
  ExternalNetworkSoftwareSummarySchema,
  ExternalNetworkAssetFilterOptionsSchema,
  ExternalNetworkAssetBreakdownQuerySchema,
  ExternalNetworkAssetBreakdownSchema,
  ExternalNetworkScanSchema,
  ExternalNetworkScanListQuerySchema,
  ExternalNetworkScanListResponseSchema,
  TriggerScanBodySchema,
  TriggerScanResponseSchema,
  CveRetestJobSchema,
  TriggerCveRetestBodySchema,
  StopScanResponseSchema,
  DeleteExternalNetworkResponseSchema,
  ScheduleExternalNetworkDeletionResponseSchema,
  ExternalNetworkSignalListQuerySchema,
  ExternalNetworkSignalListResponseSchema,
  ExternalNetworkGroupedSignalsByAssetQuerySchema,
  SurfaceGroupedSignalsByAssetResponseSchema,
  ExternalNetworkSignalAssetKindCountsResponseSchema,
  ExternalNetworkSignalCategoryByAssetKindResponseSchema,
  ExternalNetworkAssetGeoDistributionResponseSchema,
  ExternalNetworkSignalTrendQuerySchema,
  ExternalNetworkSignalTrendResponseSchema,
  AssetGraphQuerySchema,
  AssetGraphResponseSchema,
  SearchExternalNetworkEntitiesQuerySchema,
  SearchExternalNetworkEntitiesResponseSchema,
  ScanActivityLogEntrySchema,
  SurfaceFindingsListQuerySchema,
  SurfaceFindingsListResponseSchema,
  SurfaceFindingsCountQuerySchema,
  SurfaceFindingsCountResponseSchema,
  ExternalNetworkSignalsCountQuerySchema,
  ExternalNetworkSignalsCountResponseSchema,
  ExternalNetworkFullReportBodySchema,
  ExternalNetworkFullReportResponseSchema,
  NetBlockDtoSchema,
  SubnetListResponseSchema,
  DomainDtoSchema,
  HostDtoSchema,
  GatewayDetailSchema,
  NetworkServiceDtoSchema,
  SoftwareDtoSchema,
  StorageServiceDtoSchema,
  StorageServiceListQuerySchema,
  StorageServiceListResponseSchema,
  ActivityRunDtoSchema,
  ActivityRunListQuerySchema,
  ActivityRunListResponseSchema,
  NetworkGuardrailsReturnSchema,
  NetworkGuardrailCreateSchema,
  NetworkGuardrailUpdateSchema,
  NetworkGuardrailValidationResponseSchema,
  NetworkNotesReturnSchema,
  NetworkNoteCreateSchema,
  NetworkNoteUpdateSchema,
  NetworkNoteValidationResponseSchema,
  SurfaceProfileSchema,
  UpsertSurfaceProfileSchema,
  AddExternalNetworkDomainsSchema,
  AddExternalNetworkNetblocksSchema,
  AddExternalNetworkAssetsResponseSchema,
  StartProfileResearchSchema,
  ProfileResearchSchema,
  ProfileResearchJobPathParamsSchema,
  SurfaceProfilePathParamsSchema,
  ListCloudAccountPoliciesResponseSchema,
  UpdateCloudAccountPolicyBodySchema,
  CloudAccountPolicyPathParamsSchema,
  CloudAccountPolicyDtoSchema,
} from './external-network.schema';
import {
  NetBlockListQuerySchema,
  NetBlockListResponseSchema,
  DomainListQuerySchema,
  DomainListResponseSchema,
  HostListQuerySchema,
  HostListResponseSchema,
  GatewayListQuerySchema,
  GatewayListResponseSchema,
  NetworkServiceListQuerySchema,
  NetworkServiceListResponseSchema,
  SoftwareListQuerySchema,
  SoftwareListResponseSchema,
} from './external-asset-list.schema';
import {
  CloudInventorySurfaceAccountBodySchema,
  CloudInventorySurfaceProviderBodySchema,
  ListCloudInventorySurfaceAttachmentsResponseSchema,
} from './cloud-inventory-surface.schema';
import {
  DnsZonePolicyPathParamsSchema,
  DnsZoneSurfaceAttachBodySchema,
  DnsZoneSurfaceDetachBodySchema,
  DnsZoneSurfaceProviderBodySchema,
  ListDnsZoneRecordsQuerySchema,
  ListDnsZoneRecordsResponseSchema,
  ListDnsZoneSurfaceAttachmentsResponseSchema,
  UpdateDnsZonePolicyBodySchema,
  DnsZonePolicyDtoSchema,
} from './dns-zone-surface.schema';
import { EXTERNAL_NETWORK_PATH_NAME } from './external-network.constants';
import { softwareContractRoutes } from './software/software.contract';
import { scanProfileCatalogRoutes } from './scan-profile.contract';
import {
  ConfirmDetectWebAppsBodySchema,
  ConfirmDetectWebAppsResponseSchema,
  CreateWebAppClusterDraftBodySchema,
  CreateWebAppClusterDraftResponseSchema,
  LinkWebAppClusterBodySchema,
  LinkWebAppClusterResponseSchema,
  ListWebAppClustersResponseSchema,
  PreviewWebAppDraftsQuerySchema,
  PreviewWebAppDraftsResponseSchema,
} from './web-app-drafts.schema';

export const ExternalNetworkContract = terraContract
  .prefix(`/${EXTERNAL_NETWORK_PATH_NAME}`)
  .router({
    list: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List external networks',
        description:
          'Retrieves a paginated list of external networks for the current tenant.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: ExternalNetworkListQuerySchema }))
      .output(ExternalNetworkListResponseSchema),
    getAllStats: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/stats',
        summary: 'Get stats for all surfaces',
        description:
          'Returns aggregate counts for every external network in the current tenant, keyed by surface ID.',
        inputStructure: 'detailed',
      })
      .output(ExternalNetworkAllStatsResponseSchema),
    startProfileResearch: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/profile-research',
        summary: 'Start business profile research',
        description:
          'Starts an asynchronous AI research job for a company business profile.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: StartProfileResearchSchema }))
      .output(ProfileResearchSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    getProfileResearch: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/profile-research/{jobId}',
        summary: 'Get business profile research job',
        description:
          'Retrieves the latest status and output of an asynchronous business profile research job.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ProfileResearchJobPathParamsSchema }))
      .output(ProfileResearchSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    ...scanProfileCatalogRoutes,
    getById: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}',
        summary: 'Get external network by ID',
        description:
          'Retrieves a single external network with its configuration.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ExternalNetworkSchema),
    getConnectorReadiness: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/connector-readiness',
        summary: 'Get connector scan readiness',
        description:
          'Reports whether a connector-egress surface can be scanned through its bound internal network.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ConnectorReadinessSchema),
    getStats: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/stats',
        summary: 'Get surface statistics',
        description:
          'Retrieves aggregate counts of NetBlocks, Domains, Hosts, NetworkServices, Software, and signals for a surface.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ExternalNetworkStatsSchema),
    getKpiMetrics: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/metrics/kpi',
        summary: 'Get surface KPI metrics with previous values',
        description:
          'Retrieves findings, signals, and assets created in the selected period, plus the equivalent prior period, for overview trend badges. Also returns hosts, storage, and attributed domains created in the selected period grouped by cloud provider. If from and to are not provided, defaults to last 30 days.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkAssetsTrendQuerySchema,
        }),
      )
      .output(ExternalNetworkKpiMetricsResponseSchema),
    getAssetsTrend: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/assets/trend',
        summary: 'Get external network assets detection trend',
        description:
          'Returns the cumulative count of all external network assets (netblocks, domains, hosts, gateways, services, software) detected over time for a surface.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkAssetsTrendQuerySchema,
        }),
      )
      .output(ExternalNetworkAssetsTrendResponseSchema),
    getProfile: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/profile',
        summary: 'Get business profile',
        description:
          'Retrieves the business profile metadata for the external network.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: SurfaceProfilePathParamsSchema }))
      .output(SurfaceProfileSchema.nullable())
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    upsertProfile: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{surfaceId}/profile',
        summary: 'Create or update business profile',
        description:
          'Creates or updates the business profile metadata for the external network.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SurfaceProfilePathParamsSchema,
          body: UpsertSurfaceProfileSchema,
        }),
      )
      .output(SurfaceProfileSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    create: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create an external network',
        description:
          'Creates a new external network with user-provided assets and scanning configuration.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateExternalNetworkSchema }))
      .output(ExternalNetworkSchema),
    update: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{surfaceId}',
        summary: 'Update an external network',
        description:
          'Updates surface configuration including scan profile and schedule.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: UpdateExternalNetworkSchema,
        }),
      )
      .output(ExternalNetworkSchema),
    addDomains: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/assets/domains',
        summary: 'Add user-provided domains',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: AddExternalNetworkDomainsSchema,
        }),
      )
      .output(AddExternalNetworkAssetsResponseSchema),
    addNetblocks: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/assets/netblocks',
        summary: 'Add user-provided netblocks',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: AddExternalNetworkNetblocksSchema,
        }),
      )
      .output(AddExternalNetworkAssetsResponseSchema),
    delete: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{surfaceId}',
        summary: 'Delete an external network',
        description:
          'Schedules asynchronous deletion of a surface and all associated entities, scans, and signals. Active scans are stopped first. Returns immediately with a PENDING deletion status; a background worker performs the deletion in idempotent batches.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ScheduleExternalNetworkDeletionResponseSchema),
    listScans: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/scans',
        summary: 'List surface scans',
        description: 'Retrieves a paginated list of scan runs for a surface.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkScanListQuerySchema,
        }),
      )
      .output(ExternalNetworkScanListResponseSchema),
    getScan: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/scans/{scanId}',
        summary: 'Get scan details',
        description:
          'Retrieves detailed progress and stage information for a single scan.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), scanId: z.string() }),
        }),
      )
      .output(ExternalNetworkScanSchema),
    triggerScan: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/scans',
        summary: 'Trigger an ad-hoc scan',
        description:
          'Starts a new discovery and analysis scan cycle for the surface.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: TriggerScanBodySchema,
        }),
      )
      .output(TriggerScanResponseSchema),
    triggerCveRetest: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/scans/cve-retest',
        summary: 'Queue a CVE retest against matched software fingerprints',
        description:
          'Matches Software fingerprints to CVEs and queues a targeted retest job. Does not run a full discovery scan. Optional smoke test uses existing Neo execution with maxTests=1 when enabled on the surface.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: TriggerCveRetestBodySchema.optional(),
        }),
      )
      .output(CveRetestJobSchema),
    stopScan: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/scans/{scanId}/stop',
        summary: 'Stop an active scan',
        description:
          'Aborts a running scan. Already-completed stages are preserved.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), scanId: z.string() }),
        }),
      )
      .output(StopScanResponseSchema),
    getScanActivityLogs: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/scans/{scanId}/activity-logs',
        summary: 'Get scan activity logs',
        description:
          'Retrieves activity log entries for a scan. Recent entries are served from cache; older scans fall back to persisted history.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), scanId: z.string() }),
        }),
      )
      .output(ScanActivityLogEntrySchema.array()),
    listNetblocks: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/netblocks',
        summary: 'List netblocks',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: NetBlockListQuerySchema,
        }),
      )
      .output(NetBlockListResponseSchema),
    listSubnets: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/subnets',
        summary: 'List subnets',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
        }),
      )
      .output(SubnetListResponseSchema),
    getNetblock: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/netblocks/{netblockId}',
        summary: 'Get netblock by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), netblockId: z.string() }),
        }),
      )
      .output(NetBlockDtoSchema),
    updateNetblock: terraContract
      .meta({
        permissionsAll: [
          PERMISSIONS.MANAGE_APPLICATIONS.name,
          PERMISSIONS.EXECUTE_TESTS.name,
        ],
      })
      .route({
        method: 'PATCH',
        path: '/{surfaceId}/netblocks/{netblockId}',
        summary: 'Update netblock scope',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), netblockId: z.string() }),
          body: UpdateNetblockSchema,
        }),
      )
      .output(NetBlockDtoSchema),
    expandNetblock: terraContract
      .meta({
        permissionsAll: [
          PERMISSIONS.MANAGE_APPLICATIONS.name,
          PERMISSIONS.EXECUTE_TESTS.name,
        ],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/netblocks/{netblockId}/expand',
        summary: 'Trigger netblock expansion',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), netblockId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(NetBlockDtoSchema),
    listDomains: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/domains',
        summary: 'List domains',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: DomainListQuerySchema,
        }),
      )
      .output(DomainListResponseSchema),
    getDomain: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/domains/{domainId}',
        summary: 'Get domain by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), domainId: z.string() }),
        }),
      )
      .output(DomainDtoSchema),
    updateDomain: terraContract
      .meta({
        permissionsAll: [
          PERMISSIONS.MANAGE_APPLICATIONS.name,
          PERMISSIONS.EXECUTE_TESTS.name,
        ],
      })
      .route({
        method: 'PATCH',
        path: '/{surfaceId}/domains/{domainId}',
        summary: 'Update domain scope',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), domainId: z.string() }),
          body: UpdateDomainSchema,
        }),
      )
      .output(DomainDtoSchema),
    listHosts: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/hosts',
        summary: 'List hosts',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: HostListQuerySchema,
        }),
      )
      .output(HostListResponseSchema),
    getHost: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/hosts/{hostId}',
        summary: 'Get host by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), hostId: z.string() }),
        }),
      )
      .output(HostDtoSchema),
    listGateways: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/gateways',
        summary: 'List gateways',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: GatewayListQuerySchema,
        }),
      )
      .output(GatewayListResponseSchema),
    getGateway: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/gateways/{gatewayId}',
        summary: 'Get gateway by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), gatewayId: z.string() }),
        }),
      )
      .output(GatewayDetailSchema),
    listStorageServices: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/storage-services',
        summary: 'List storage services',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: StorageServiceListQuerySchema,
        }),
      )
      .output(StorageServiceListResponseSchema),
    getStorageService: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/storage-services/{storageServiceId}',
        summary: 'Get storage service by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            surfaceId: z.string(),
            storageServiceId: z.string(),
          }),
        }),
      )
      .output(StorageServiceDtoSchema),
    listServices: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/services',
        summary: 'List network services',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: NetworkServiceListQuerySchema,
        }),
      )
      .output(NetworkServiceListResponseSchema),
    getService: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/services/{serviceId}',
        summary: 'Get service by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), serviceId: z.string() }),
        }),
      )
      .output(NetworkServiceDtoSchema),
    listSoftware: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/software',
        summary: 'List software',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: SoftwareListQuerySchema,
        }),
      )
      .output(SoftwareListResponseSchema),
    getSoftwareSummary: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/software/summary',
        summary: 'Get software inventory summary',
        description:
          'Returns software counts by type and the most common products on a surface.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ExternalNetworkSoftwareSummarySchema),
    getAssetFilterOptions: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/asset-filter-options',
        summary: 'List distinct asset filter options',
        description:
          'Returns distinct software products, CVE IDs, regions, and organizations for surface asset filters.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ExternalNetworkAssetFilterOptionsSchema),
    getAssetBreakdown: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/asset-breakdown',
        summary: 'Get asset breakdown counts',
        description:
          'Returns closed-set composition counts for a surface asset kind.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkAssetBreakdownQuerySchema,
        }),
      )
      .output(ExternalNetworkAssetBreakdownSchema),
    getSoftware: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/software/{softwareId}',
        summary: 'Get software by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), softwareId: z.string() }),
        }),
      )
      .output(SoftwareDtoSchema),
    ...softwareContractRoutes,
    listActivityRuns: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/activity-runs',
        summary: 'List activity runs for a surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ActivityRunListQuerySchema,
        }),
      )
      .output(ActivityRunListResponseSchema),
    getActivityRun: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/activity-runs/{runId}',
        summary: 'Get activity run by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), runId: z.string() }),
        }),
      )
      .output(ActivityRunDtoSchema),
    listSignals: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/signals',
        summary: 'List surface signals',
        description:
          'Retrieves a paginated list of signals for a surface, optionally filtered by category, target entity kind, or specific target.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkSignalListQuerySchema,
        }),
      )
      .output(ExternalNetworkSignalListResponseSchema),
    countSignals: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/signals/count',
        summary: 'Count surface signals',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkSignalsCountQuerySchema,
        }),
      )
      .output(ExternalNetworkSignalsCountResponseSchema),
    listSignalsGroupedByAsset: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/signals/grouped-by-asset',
        summary: 'List surface signals grouped by asset',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkGroupedSignalsByAssetQuerySchema,
        }),
      )
      .output(SurfaceGroupedSignalsByAssetResponseSchema),
    getSignalAssetKindCounts: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/signals/asset-kind-counts',
        summary: 'Get signal counts by asset kind',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkSignalListQuerySchema.pick({
            category: true,
            stage: true,
            status: true,
            confirmedVulnerabilityFindingState: true,
            origin: true,
            source: true,
            importBatchId: true,
            createdStartDate: true,
            createdEndDate: true,
          }),
        }),
      )
      .output(ExternalNetworkSignalAssetKindCountsResponseSchema),
    getSignalCategoryByAssetKind: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/signals/category-by-asset-kind',
        summary: 'Get signal counts by category and asset kind',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
        }),
      )
      .output(ExternalNetworkSignalCategoryByAssetKindResponseSchema),
    getAssetGeoDistribution: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/assets/geo-distribution',
        summary: 'Get host counts by geographic country',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
        }),
      )
      .output(ExternalNetworkAssetGeoDistributionResponseSchema),
    getSignalsTrend: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/signals/trend',
        summary: 'Get surface signal trend',
        description:
          'Returns daily signal counts for the surface over the requested window.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: ExternalNetworkSignalTrendQuerySchema,
        }),
      )
      .output(ExternalNetworkSignalTrendResponseSchema),
    listFindings: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/findings',
        summary: 'List findings for a surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: SurfaceFindingsListQuerySchema,
        }),
      )
      .output(SurfaceFindingsListResponseSchema),
    countFindings: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/findings/count',
        summary: 'Count findings for a surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: SurfaceFindingsCountQuerySchema,
        }),
      )
      .output(SurfaceFindingsCountResponseSchema),
    createFullReport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/full-report',
        summary: 'Create a full report for selected surface findings',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: ExternalNetworkFullReportBodySchema,
        }),
      )
      .output(ExternalNetworkFullReportResponseSchema),
    getAssetGraph: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/graph',
        summary: 'Get entity relationship graph',
        description:
          'Returns nodes (typed by entity kind) and edges (derived from FK relations) for graph visualization.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: AssetGraphQuerySchema,
        }),
      )
      .output(AssetGraphResponseSchema),
    searchExternalNetworkEntities: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/entities/search',
        summary: 'Search external network entities for graph exploration',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: SearchExternalNetworkEntitiesQuerySchema,
        }),
      )
      .output(SearchExternalNetworkEntitiesResponseSchema),
    getNetworkGuardrails: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/guardrails',
        summary: 'List network guardrails',
        description:
          'Retrieves all security guardrails configured for the surface.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(NetworkGuardrailsReturnSchema),
    createNetworkGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/guardrails',
        summary: 'Create network guardrail',
        description:
          'Creates a new security guardrail with custom rules and actions for the surface.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: NetworkGuardrailCreateSchema,
        }),
      )
      .output(NetworkGuardrailValidationResponseSchema),
    updateNetworkGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{surfaceId}/guardrails/{guardrailId}',
        summary: 'Update network guardrail',
        description: 'Updates an existing surface guardrail configuration.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), guardrailId: z.string() }),
          body: NetworkGuardrailUpdateSchema,
        }),
      )
      .output(NetworkGuardrailValidationResponseSchema),
    deleteNetworkGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{surfaceId}/guardrails/{guardrailId}',
        summary: 'Delete network guardrail',
        description: 'Permanently removes a guardrail from the surface.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), guardrailId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(DeleteExternalNetworkResponseSchema),
    getNetworkNotes: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/notes',
        summary: 'List surface notes',
        description:
          'Retrieves all notes and testing instructions for the surface.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(NetworkNotesReturnSchema),
    createNetworkNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/notes',
        summary: 'Create surface note',
        description: 'Adds a new note or testing instruction to the surface.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: NetworkNoteCreateSchema,
        }),
      )
      .output(NetworkNoteValidationResponseSchema),
    updateNetworkNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{surfaceId}/notes/{noteId}',
        summary: 'Update surface note',
        description: 'Updates an existing surface note.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), noteId: z.string() }),
          body: NetworkNoteUpdateSchema,
        }),
      )
      .output(NetworkNoteValidationResponseSchema),
    deleteNetworkNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{surfaceId}/notes/{noteId}',
        summary: 'Delete surface note',
        description: 'Permanently removes a note from the surface.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string(), noteId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(DeleteExternalNetworkResponseSchema),
    listCloudAccountPolicies: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/cloud-account-policies',
        summary: 'List cloud account policies',
        description:
          'Lists connected cloud inventory accounts and their auto-scope policy for this surface.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ListCloudAccountPoliciesResponseSchema),
    updateCloudAccountPolicy: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{surfaceId}/cloud-account-policies/{accountId}',
        summary: 'Update cloud account policy',
        description:
          'Sets auto-scope for a connected cloud account on this surface.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: CloudAccountPolicyPathParamsSchema,
          body: UpdateCloudAccountPolicyBodySchema,
        }),
      )
      .output(CloudAccountPolicyDtoSchema),
    listCloudInventoryAttachments: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/cloud-inventory',
        summary: 'List cloud inventory attachments',
        description:
          'Lists discovered cloud inventory accounts and whether they are attached to this surface.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ListCloudInventorySurfaceAttachmentsResponseSchema),
    attachCloudInventory: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/cloud-inventory/attach',
        summary: 'Attach cloud inventory',
        description:
          'Attaches a discovered cloud inventory account to this surface and syncs that provider.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: CloudInventorySurfaceAccountBodySchema,
        }),
      )
      .output(ListCloudInventorySurfaceAttachmentsResponseSchema),
    detachCloudInventory: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{surfaceId}/cloud-inventory/attach',
        summary: 'Detach cloud inventory',
        description:
          'Removes a cloud inventory account from this surface and deprovisions its cloud-owned assets here.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: CloudInventorySurfaceAccountBodySchema,
        }),
      )
      .output(ListCloudInventorySurfaceAttachmentsResponseSchema),
    syncCloudInventory: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/cloud-inventory/sync',
        summary: 'Sync cloud inventory',
        description:
          'Runs inventory sync for a connected cloud provider. Discovered accounts apply only where they are attached.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: CloudInventorySurfaceProviderBodySchema,
        }),
      )
      .output(ListCloudInventorySurfaceAttachmentsResponseSchema),
    listWebAppClusters: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/web-app-clusters',
        summary: 'List web application clusters',
        description:
          'Returns similarity clusters of discovered web applications on this surface.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ListWebAppClustersResponseSchema),
    previewWebAppDrafts: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/web-app-drafts/preview',
        summary: 'Preview web application draft clusters',
        description:
          'Computes draft-application clusters without persisting the detect-web-apps toggle.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          query: PreviewWebAppDraftsQuerySchema.optional(),
        }),
      )
      .output(PreviewWebAppDraftsResponseSchema),
    confirmDetectWebApps: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/web-app-drafts/confirm',
        summary:
          'Review discovered web-app clusters and optionally auto-create',
        description:
          'Creates or links selected clusters. Auto-create on future scans is enabled only when requested.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: ConfirmDetectWebAppsBodySchema,
        }),
      )
      .output(ConfirmDetectWebAppsResponseSchema),
    linkWebAppCluster: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/web-app-clusters/link',
        summary: 'Link a web application cluster to an existing application',
        description:
          'Attaches a discovered cluster on this surface to an existing web application without creating a draft.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: LinkWebAppClusterBodySchema,
        }),
      )
      .output(LinkWebAppClusterResponseSchema),
    createWebAppClusterDraft: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/web-app-clusters/create-draft',
        summary: 'Create a draft web application from a cluster',
        description:
          'Creates a draft web application for a discovered customer-owned cluster that is not yet linked.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: CreateWebAppClusterDraftBodySchema,
        }),
      )
      .output(CreateWebAppClusterDraftResponseSchema),
    listDnsZoneAttachments: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/dns-zones',
        summary: 'List DNS zone attachments',
        description:
          'Lists discovered authoritative DNS zones and whether they are attached to this surface.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
      .output(ListDnsZoneSurfaceAttachmentsResponseSchema),
    attachDnsZone: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/dns-zones/attach',
        summary: 'Attach DNS zone',
        description:
          'Attaches a discovered DNS zone to this surface, adds its apex, and syncs that provider.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: DnsZoneSurfaceAttachBodySchema,
        }),
      )
      .output(ListDnsZoneSurfaceAttachmentsResponseSchema),
    detachDnsZone: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{surfaceId}/dns-zones/attach',
        summary: 'Detach DNS zone',
        description: 'Removes a DNS zone from this surface.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: DnsZoneSurfaceDetachBodySchema,
        }),
      )
      .output(ListDnsZoneSurfaceAttachmentsResponseSchema),
    syncDnsZones: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/dns-zones/sync',
        summary: 'Sync DNS zones',
        description:
          'Runs DNS zone sync for a connected cloud provider. Attached zones are projected into this surface.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ surfaceId: z.string() }),
          body: DnsZoneSurfaceProviderBodySchema,
        }),
      )
      .output(ListDnsZoneSurfaceAttachmentsResponseSchema),
    verifyDnsZoneDelegation: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{surfaceId}/dns-zones/{zoneId}/verify-delegation',
        summary: 'Verify DNS zone delegation',
        description:
          'Re-checks provider nameservers against the live parent delegation for this zone.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: DnsZonePolicyPathParamsSchema }))
      .output(ListDnsZoneSurfaceAttachmentsResponseSchema),
    updateDnsZonePolicy: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{surfaceId}/dns-zones/{zoneId}',
        summary: 'Update DNS zone policy',
        description:
          'Sets enabled state, apex auto-scope, and record exclusion patterns for an attached zone.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: DnsZonePolicyPathParamsSchema,
          body: UpdateDnsZonePolicyBodySchema,
        }),
      )
      .output(DnsZonePolicyDtoSchema),
    listDnsZoneRecords: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{surfaceId}/dns-zones/{zoneId}/records',
        summary: 'List DNS zone records',
        description:
          'Lists authoritative records stored for an attached or catalogued DNS zone.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: DnsZonePolicyPathParamsSchema,
          query: ListDnsZoneRecordsQuerySchema.optional().default({}),
        }),
      )
      .output(ListDnsZoneRecordsResponseSchema),
  });
